/**
 * Base types and enums for player configurations
 */

/**
 * Asset types - aligned with backend AssetType enum.
 *
 * Playable types (supported by players):
 * - VIDEO: Video files (mp4, webm, etc.) - uses VideoPlayer
 * - AUDIO: Audio files (mp3, wav, etc.) - uses AudioPlayer
 * - IMAGE: Image files (jpg, png, etc.) - uses ImagePlayer
 * - DOCUMENT: Documents (pdf, etc.) - uses PDFPlayer
 *
 * Non-playable types (not supported by players):
 * - FOLDER: Directory/folder container
 * - FILE: Generic file without specific type
 * - ARCHIVE: Compressed archives (zip, rar, etc.)
 * - COLLECTION: Playlist, album, gallery, etc.
 * - SUBTITLES: Subtitle files (srt, vtt, etc.)
 * - OTHER: Unrecognized file types
 */
export enum AssetType {
  // Playable types
  VIDEO = 'video',
  AUDIO = 'audio',
  IMAGE = 'image',
  DOCUMENT = 'document',
  THIRD_PARTY = 'third_party',

  // Non-playable types
  FOLDER = 'folder',
  FILE = 'file',
  ARCHIVE = 'archive',
  COLLECTION = 'collection',
  SUBTITLES = 'subtitles',
  OTHER = 'other',
}

/** Subset of AssetType that can be played by a player */
export type PlayableAssetType =
  | AssetType.VIDEO
  | AssetType.AUDIO
  | AssetType.IMAGE
  | AssetType.DOCUMENT
  | AssetType.THIRD_PARTY

/** Array of playable asset types for runtime checks */
export const PLAYABLE_ASSET_TYPES: PlayableAssetType[] = [
  AssetType.VIDEO,
  AssetType.AUDIO,
  AssetType.IMAGE,
  AssetType.DOCUMENT,
  AssetType.THIRD_PARTY,
]

/** Type guard to check if an asset type is playable */
export function isPlayableAssetType(type: AssetType): type is PlayableAssetType {
  return PLAYABLE_ASSET_TYPES.includes(type as PlayableAssetType)
}

/** Branded type for ISO 8601 timestamp strings */
export type Timestamp = string & { readonly __brand: 'Timestamp' }

/** Branded type for UUID strings */
export type UUID = string & { readonly __brand: 'UUID' }

/** Branded type for heatmap values (0-255 range) */
export type HeatmapValue = number & { readonly __brand: 'HeatmapValue' }

export interface Dimensions {
  width: number
  height: number
}

export interface TimeRange {
  start: number // seconds
  end: number // seconds
}

/**
 * The buffered range the playhead is inside, or none.
 *
 * **A media element's `buffered` genuinely holds several disjoint ranges, and painting them all
 * is what makes a progress bar look broken.** After a seek it keeps the range around where the
 * viewer was *and* the one being filled where they landed, so the bar grows islands that are
 * accurate and unreadable — what a progress bar answers is "how much is ready ahead of me", and
 * a range the playhead is not in answers a different question. Every mainstream player shows
 * this one alone.
 *
 * `undefined` when nothing is ready at the playhead, which is the honest answer in the moment
 * after a seek: the bar shows no buffer rather than somebody else's.
 */
export function bufferedAt(ranges: TimeRange[], time: number): TimeRange | undefined {
  return ranges.find((range) => range.start <= time && time <= range.end)
}

export interface SignedUrl {
  url: string
  expiresAt: Timestamp
}

// =============================================================================
// ERROR TYPES
// =============================================================================

export type PlayerErrorCode =
  | 'NETWORK_ERROR'
  | 'DECODE_ERROR'
  | 'FORMAT_NOT_SUPPORTED'
  | 'PLAYBACK_ERROR'
  | 'DRM_ERROR'
  | 'PERMISSION_DENIED'
  | 'ASSET_NOT_FOUND'
  | 'PLAYBACK_INFO_EXPIRED'
  | 'PLAYBACK_INFO_FETCH_FAILED'
  | 'UNKNOWN'

export interface PlayerError {
  code: PlayerErrorCode
  message: string
  originalError?: Error
  assetId?: UUID
  recoverable: boolean
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function createUUID(value: string): UUID {
  return value as UUID
}

export function createTimestamp(value: string): Timestamp {
  return value as Timestamp
}

export function createHeatmapValue(value: number): HeatmapValue {
  if (value < 0 || value > 255) {
    throw new RangeError('Heatmap value must be between 0 and 255')
  }
  return value as HeatmapValue
}
